/* ------------------------------------------------------------------ *
   Platform mega-menu data + LexOS page content
   - `status`: "live" (in production) | "beta" | "soon"
   - `features`: 2-3 bullet points shown on the LexOS page card
   ------------------------------------------------------------------ */

export const LEXOS_PILLARS = [
    {
        id: "lexos",
        name: "LexOPS Platform",
        icon: "fa-microchip",
        tagline: "The operating system for legal ops",
        status: "beta",
        accent: true,
        features: [
            "Unified workspace across intake, cases, and follow-ups",
            "Every agent's actions logged, replayable, and auditable",
            "Native integrations with SmartAdvocate, Filevine, Litify",
        ],
    },
    {
        id: "atlas",
        name: "LexGraph",
        icon: "fa-folder-tree",
        tagline: "Orchestration of intelligence across every matter",
        status: "soon",
        features: [
            "Continuously synthesizes documents, calls, and notes",
            "Routes context to the right agent — and the right human",
            "Answers any question about a matter in plain English",
        ],
    },
    {
        id: "auditor",
        name: "Auditor",
        icon: "fa-clipboard-check",
        tagline: "Nightly review of every matter",
        status: "soon",
        features: [
            "Flags overdue tasks, missing records, and at-risk cases",
            "Daily digest delivered to your case managers by 7 AM",
        ],
    },
    {
        id: "analytics",
        name: "Analytics",
        icon: "fa-chart-line",
        tagline: "Live firm-wide intelligence",
        status: "soon",
        features: [
            "Real-time dashboards on intake, conversion, and throughput",
            "Cohort views by source, practice area, and attorney",
        ],
    },
];

export const AGENT_FIRST = [
    {
        id: "intake",
        name: "Intake Agent",
        icon: "fa-headset",
        tagline: "Answers every call · 24/7 · 28 languages",
        status: "live",
        features: [
            "Real-time conflict checks against your CRM",
            "Structured intake form pushed into SmartAdvocate / Filevine",
            "Auto-routes Spanish, Portuguese, Mandarin, and 25 more",
        ],
    },
    {
        id: "voicemail",
        name: "Voicemail Recovery",
        icon: "fa-voicemail",
        tagline: "Transcribes, qualifies, and routes overnight",
        status: "live",
        features: [
            "Overnight voicemails triaged into your CRM by 7 AM",
            "Auto-replies to urgent cases with retainer link",
            "Recovers 18-25% of previously missed leads",
        ],
    },
    {
        id: "followup",
        name: "Follow-up Agent",
        icon: "fa-clipboard-list",
        tagline: "Chases retainers, records, and signatures",
        status: "live",
        features: [
            "SMS + email cadence personalized per matter type",
            "Auto-pauses on reply and pings the assigned paralegal",
            "Cuts retainer-signing time from 9 days to under 48 hours",
        ],
    },
    {
        id: "outbound",
        name: "Outbound Caller",
        icon: "fa-phone-volume",
        tagline: "Status updates, scheduling, claim openings",
        status: "live",
        features: [
            "Calls hundreds of clients per day with personalized scripts",
            "Books availability windows directly into your calendar",
            "Logs every conversation back into the case file",
        ],
    },
    {
        id: "concierge",
        name: "After-Hours Concierge",
        icon: "fa-moon",
        tagline: "Captures every lead while you sleep",
        status: "live",
        features: [
            "Handles inbound traffic from 6 PM to 8 AM and weekends",
            "Same playbook as your daytime intake team",
            "Books warm leads onto the next-morning attorney calendar",
        ],
    },
];

export const HUMAN_FIRST = [
    {
        id: "case-console",
        name: "Case Manager Console",
        icon: "fa-folder-tree",
        tagline: "Prioritized work queue, every morning",
        status: "beta",
        features: [
            "AI-ranked case list by deadline, value, and risk",
            "One-click context: every call, doc, and prior action",
        ],
    },
    {
        id: "paralegal-copilot",
        name: "Paralegal Co-Pilot",
        icon: "fa-user-tie",
        tagline: "Drafts demands, discovery, status updates",
        status: "beta",
        features: [
            "Generates first drafts in your firm's tone",
            "Pulls case facts, citations, and exhibits automatically",
        ],
    },
    {
        id: "brief-builder",
        name: "Attorney Brief Builder",
        icon: "fa-gavel",
        tagline: "Citations + arguments, in your voice",
        status: "soon",
        features: [
            "Drafts motions and briefs with verified citations",
            "Learns your firm's preferred arguments and structures",
        ],
    },
    {
        id: "status-composer",
        name: "Status Update Composer",
        icon: "fa-envelope-open-text",
        tagline: "Client communications, batch-ready",
        status: "live",
        features: [
            "Generates personalized status updates for every matter",
            "Reviewable in batch — approve 50 updates in 5 minutes",
        ],
    },
];

/* Status display config */
export const STATUS_META = {
    live: { label: "LIVE",        color: "#15803D", bg: "rgba(22,163,74,0.10)",  dot: "#22C55E" },
    beta: { label: "EARLY ACCESS", color: "#B45309", bg: "rgba(180,83,9,0.10)",  dot: "#F59E0B" },
    soon: { label: "COMING SOON", color: "var(--ink-dim)", bg: "rgba(35,31,35,0.06)", dot: "var(--ink-dim)" },
};
